/**
 * DateRangePicker Component
 * Allows selection of custom date range for viewing/exporting data
 */
import React, { useState } from 'react';
import { Calendar, X } from 'lucide-react';
import { Button } from '../ui/button';
import { DAY_NAMES_FULL, MONTH_NAMES } from './constants';

// Helper to format date as DD-MM-YY
const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = String(d.getFullYear()).slice(-2);
  return `${day}-${month}-${year}`;
};

// Helper to get day of week name in Greek
const getDayName = (date) => {
  if (!date) return '';
  const d = new Date(date);
  // getDay() returns 0 for Sunday, we need to adjust
  const dayIndex = d.getDay() === 0 ? 6 : d.getDay() - 1;
  return DAY_NAMES_FULL[dayIndex];
};

// Helper to format date range label
export const formatDateRangeLabel = (startDate, endDate) => {
  if (!startDate || !endDate) return 'Επιλέξτε ημερομηνίες';
  return `${getDayName(startDate)} ${formatDate(startDate)} - ${getDayName(endDate)} ${formatDate(endDate)}`;
};

const DateRangePicker = ({ startDate, endDate, onApply, onClose }) => {
  const [selectedStart, setSelectedStart] = useState(startDate || '');
  const [selectedEnd, setSelectedEnd] = useState(endDate || '');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Get days in month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get first day of month (0 = Monday in our system)
  const getFirstDayOfMonth = (year, month) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1; // Convert Sunday=0 to Monday=0
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    
    const days = [];
    
    // Empty cells for days before first of month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    
    // Days of month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const handleDateClick = (date) => {
    if (!date) return;
    
    const dateStr = date.toISOString().split('T')[0];
    
    if (!selectedStart || (selectedStart && selectedEnd)) {
      // Start new selection
      setSelectedStart(dateStr);
      setSelectedEnd('');
    } else {
      // Complete selection
      if (new Date(dateStr) < new Date(selectedStart)) {
        setSelectedEnd(selectedStart);
        setSelectedStart(dateStr);
      } else {
        setSelectedEnd(dateStr);
      }
    }
  };

  const isSelected = (date) => {
    if (!date) return false;
    const dateStr = date.toISOString().split('T')[0];
    return dateStr === selectedStart || dateStr === selectedEnd;
  };

  const isInRange = (date) => {
    if (!date || !selectedStart || !selectedEnd) return false;
    const dateStr = date.toISOString().split('T')[0];
    return dateStr > selectedStart && dateStr < selectedEnd;
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isFuture = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date > today;
  };

  const handleApply = () => {
    if (selectedStart && selectedEnd) {
      onApply(selectedStart, selectedEnd);
    }
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  // Quick presets
  const presets = [
    { label: 'Τελευταία εβδομάδα', days: 7 },
    { label: 'Τελευταίες 2 εβδομάδες', days: 14 },
    { label: 'Τελευταίες 3 εβδομάδες', days: 21 },
    { label: 'Τελευταίος μήνας', days: 30 },
  ];

  const applyPreset = (days) => {
    const end = new Date();
    end.setDate(end.getDate() - 1); // Yesterday (exclude today)
    const start = new Date(end);
    start.setDate(start.getDate() - days + 1);
    
    setSelectedStart(start.toISOString().split('T')[0]);
    setSelectedEnd(end.toISOString().split('T')[0]);
  };

  const days = generateCalendarDays();

  return (
    <div className="absolute top-full right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-4 z-50 w-[360px]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Επιλογή Ημερομηνιών
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Quick presets */}
      <div className="flex flex-wrap gap-1 mb-4">
        {presets.map((preset) => (
          <button
            key={preset.days}
            onClick={() => applyPreset(preset.days)}
            className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 hover:bg-blue-100 dark:hover:bg-blue-900 text-gray-700 dark:text-gray-300"
          >
            {preset.label}
          </button>
        ))}
      </div>

      {/* Month navigation */}
      <div className="flex items-center justify-between mb-3">
        <button onClick={prevMonth} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
          ←
        </button>
        <span className="font-medium text-gray-900 dark:text-white">
          {MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        <button onClick={nextMonth} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
          →
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Δε', 'Τρ', 'Τε', 'Πε', 'Πα', 'Σα', 'Κυ'].map((day) => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((date, idx) => (
          <button
            key={idx}
            onClick={() => handleDateClick(date)}
            disabled={!date || isFuture(date)}
            className={`
              p-2 text-sm rounded transition-colors
              ${!date ? 'invisible' : ''}
              ${isFuture(date) ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' : ''}
              ${isSelected(date) ? 'bg-blue-500 text-white' : ''}
              ${isInRange(date) ? 'bg-blue-100 dark:bg-blue-900/30' : ''}
              ${isToday(date) && !isSelected(date) ? 'border-2 border-blue-500' : ''}
              ${!isSelected(date) && !isInRange(date) && !isFuture(date) && date ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : ''}
            `}
          >
            {date?.getDate()}
          </button>
        ))}
      </div>

      {/* Selected range display */}
      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Επιλεγμένο διάστημα:</div>
        <div className="text-sm font-medium text-gray-900 dark:text-white">
          {selectedStart && selectedEnd ? (
            <>
              <span className="text-blue-600 dark:text-blue-400">{getDayName(selectedStart)}</span> {formatDate(selectedStart)}
              <span className="mx-2">→</span>
              <span className="text-blue-600 dark:text-blue-400">{getDayName(selectedEnd)}</span> {formatDate(selectedEnd)}
            </>
          ) : selectedStart ? (
            <>Επιλέξτε τελική ημερομηνία</>
          ) : (
            <>Επιλέξτε αρχική ημερομηνία</>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 mt-4">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onClose}
          className="flex-1"
        >
          Ακύρωση
        </Button>
        <Button 
          size="sm" 
          onClick={handleApply}
          disabled={!selectedStart || !selectedEnd}
          className="flex-1"
        >
          Εφαρμογή
        </Button>
      </div>
    </div>
  );
};

export default DateRangePicker;
